import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/* ------------------------------------------------------------------
   Full-screen pricing finder. Two ways through it:
     browse — what are you pricing -> category -> foundation -> page
     guide  — category -> budget -> functions -> a recommendation
   ------------------------------------------------------------------ */

type Dest = { to: string; label: string };

const CSS = `
.pfx { position:fixed; inset:0; z-index:2000; display:grid; place-items:center;
  padding:24px; overflow-y:auto;
  background:rgba(6,6,5,.90); backdrop-filter:blur(18px) saturate(.75) brightness(.55);
  -webkit-backdrop-filter:blur(18px) saturate(.75) brightness(.55);
  animation:pfxIn .28s cubic-bezier(.4,0,.2,1); font-family:'Inter',system-ui,sans-serif; }
@keyframes pfxIn { from { opacity:0 } to { opacity:1 } }
.pfx .sheet { width:min(1080px,100%); margin:auto; animation:pfxUp .34s cubic-bezier(.4,0,.2,1); }
@keyframes pfxUp { from { opacity:0; transform:translateY(14px) } to { opacity:1; transform:none } }
.pfx .kick { font-size:.72rem; letter-spacing:.16em; text-transform:uppercase; font-weight:700;
  color:#d8c48d; margin:0 0 8px; }
.pfx h2 { font-family:'Libre Baskerville',Georgia,serif; color:#fff; margin:0 0 6px;
  font-size:clamp(1.5rem,3.4vw,2.3rem); line-height:1.2; }
.pfx .sub { color:#ddd7ca; margin:0 0 26px; line-height:1.6; max-width:62ch; }
.pfx .opts { display:grid; gap:14px; grid-template-columns:repeat(auto-fit,minmax(210px,1fr)); }
.pfx .opt { background:rgba(255,255,255,.07); border:1px solid rgba(255,255,255,.22);
  border-radius:16px; padding:22px; text-align:left; cursor:pointer; color:#fff; font:inherit;
  transition:background .2s, border-color .2s, transform .2s; }
.pfx .opt:hover { background:rgba(216,196,141,.13); border-color:#d8c48d; transform:translateY(-3px); }
.pfx .opt.on { border-color:#d8c48d; background:rgba(216,196,141,.16); }
.pfx .opt b { display:block; font-family:'Libre Baskerville',Georgia,serif; font-size:1.16rem;
  font-weight:400; margin-bottom:6px; }
.pfx .opt span { display:block; color:#cfc8ba; font-size:.87rem; line-height:1.55; }
.pfx .opt em { display:block; color:#d8c48d; font-style:normal; font-weight:700; font-size:.9rem; margin-top:9px; }
.pfx .tagrec { display:inline-block; background:#1e7a45; color:#fff; font-size:.62rem; font-weight:700;
  letter-spacing:.1em; padding:3px 9px; border-radius:999px; margin-bottom:9px; }
.pfx .bar { display:flex; gap:14px; align-items:center; margin-top:26px; flex-wrap:wrap; }
.pfx .lnk { background:none; border:0; color:#d8c48d; cursor:pointer; font:inherit;
  text-decoration:underline; text-underline-offset:4px; padding:0; }
.pfx .x { position:fixed; top:18px; right:20px; width:42px; height:42px; border-radius:50%;
  background:rgba(255,255,255,.1); border:1px solid rgba(255,255,255,.2); color:#fff;
  font-size:1.3rem; cursor:pointer; line-height:1; }
.pfx .x:hover { background:rgba(255,255,255,.2); }
.pfx .crumbs { color:#8f8878; font-size:.8rem; margin-bottom:14px; }
.pfx .crumbs b { color:#d8c48d; font-weight:600; }
.pfx .chk { display:flex; gap:11px; align-items:flex-start; padding:13px 15px; cursor:pointer;
  border:1px solid rgba(255,255,255,.14); border-radius:11px; background:rgba(255,255,255,.04);
  color:#fff; text-align:left; font:inherit; transition:border-color .18s, background .18s; }
.pfx .chk.on { border-color:#d8c48d; background:rgba(216,196,141,.14); }
.pfx .chk i { flex:none; width:19px; height:19px; border-radius:5px; border:1px solid #8f8878;
  display:grid; place-items:center; font-style:normal; font-size:.72rem; margin-top:1px; }
.pfx .chk.on i { background:#d8c48d; border-color:#d8c48d; color:#111; }
.pfx .chk span { font-size:.9rem; line-height:1.45; }
.pfx .grid3 { display:grid; gap:10px; grid-template-columns:repeat(auto-fit,minmax(230px,1fr)); }
.pfx .go { background:#d8c48d; color:#111; border:0; border-radius:9px; padding:14px 30px;
  font-weight:700; font-size:1rem; cursor:pointer; font-family:inherit; }
.pfx .go[disabled] { opacity:.4; cursor:not-allowed; }
.pfx .rec { background:rgba(255,255,255,.06); border:1px solid #d8c48d; border-radius:16px; padding:26px; }
.pfx .rec .why { color:#c9c3b6; line-height:1.7; margin:12px 0 0; }
.pfx .rec .why b { color:#fff; }
@media (prefers-reduced-motion:reduce){ .pfx, .pfx .sheet { animation:none } .pfx .opt:hover { transform:none } }
`;

const FEATURES: Record<string, { id: string; label: string }[]> = {
  ngo: [
    { id: 'donations',  label: 'Take donations online' },
    { id: 'receipts',   label: '80G receipts issued automatically' },
    { id: 'compliance', label: '10BD / 10BE filing help' },
    { id: 'donorcrm',   label: 'A donor record with giving history' },
    { id: 'members',    label: 'Membership with renewals' },
    { id: 'cases',      label: 'Beneficiary case pages' },
    { id: 'campaigns',  label: 'Campaign pages with a goal tracker' },
    { id: 'whatsapp',   label: 'WhatsApp messaging to donors' },
    { id: 'volunteers', label: 'Volunteer sign-up' },
    { id: 'brochure',   label: 'Only a presence — no donations online' },
    { id: 'custom',     label: 'A design and behaviour entirely mine' },
  ],
  ecom: [
    { id: 'store',    label: 'Products, cart and checkout' },
    { id: 'cod',      label: 'Cash on delivery, full or partial' },
    { id: 'coupons',  label: 'Coupons, offers and loyalty' },
    { id: 'returns',  label: 'Returns and refunds handled in-system' },
    { id: 'bookings', label: 'Bookings or appointments' },
    { id: 'tracking', label: 'Order tracking for the customer' },
    { id: 'own',      label: 'No monthly platform fee — I want to own it' },
    { id: 'fast',     label: 'Live as fast as possible' },
    { id: 'small',    label: 'A small catalogue, nothing unusual' },
    { id: 'custom',   label: 'Behaviour no platform will let me build' },
  ],
  other: [
    { id: 'pages',    label: 'Service pages and a contact form' },
    { id: 'blog',     label: 'Blog or news I publish myself' },
    { id: 'gallery',  label: 'Gallery, team and testimonials' },
    { id: 'catalogue',label: 'A catalogue people browse and enquire about' },
    { id: 'events',   label: 'Events or appointments with registration' },
    { id: 'routing',  label: 'Enquiries routed to different inboxes' },
    { id: 'multiadmin',label: 'Several staff logging in separately' },
    { id: 'portal',   label: 'A portal customers log into' },
    { id: 'integrate',label: 'Integration with software we already run' },
    { id: 'custom',   label: 'A design and behaviour entirely mine' },
  ],
};

const INDUSTRIES = [
  { id: 'ngo',   b: 'Non-profit or NGO',  s: 'Donations, causes, volunteers, receipts, compliance' },
  { id: 'ecom',  b: 'E-commerce',         s: 'Products, cart, checkout, delivery' },
  { id: 'other', b: 'Every other industry', s: 'Clinics, builders, manufacturers, consultants, schools, hospitality' },
];



/* Recommendation is biased by industry, deliberately:
   an NGO leans to Give Setu, a store leans to WooCommerce or coded, and
   Shopify is only suggested when speed and simplicity are what was asked for. */
function recommend(industry: string, fns: string[]): { to: string; label: string; title: string; price: string; why: string } {
  const has = (x: string) => fns.includes(x);

  if (industry === 'ngo') {
    const systemish = ['receipts', 'compliance', 'donorcrm', 'members', 'cases', 'campaigns', 'whatsapp'].filter(has).length;
    if (has('brochure') && systemish === 0) {
      return { to: '/pricing/ngo-website', label: 'See the WordPress NGO build',
        title: 'WordPress NGO website', price: '₹35,000 + GST, one-time',
        why: 'You want the organisation represented properly, not a donation engine behind it. A one-time WordPress build does exactly that, and you own it outright.' };
    }
    if (has('custom') && systemish <= 1) {
      return { to: '/pricing/ngo-website', label: 'See the custom-coded option',
        title: 'Custom-coded NGO site', price: 'Quoted to scope',
        why: 'You want the design and behaviour to be entirely yours, without much of the receipting machinery. That is a coded build, quoted against your scope.' };
    }
    return { to: '/pricing/ngo-os', label: 'See Give Setu', title: 'Give Setu',
      price: 'From ₹25,000 a year + GST',
      why: systemish >= 3
        ? 'What you have described is not website work — it is an operating system. Receipts, compliance, donors and campaigns all belong in one register, and the website comes inside the plan rather than as a separate bill.'
        : 'Even for donations alone, the receipt is the hard part — numbered, immutable, and defensible in an assessment. Give Setu issues it in under four seconds, takes 0% commission, and includes the website in the plan.' };
  }

  if (industry === 'ecom') {
    if (has('custom')) {
      return { to: '/pricing/websites?track=coded-ecom', label: 'See the custom-coded build',
        title: 'Custom-coded store', price: '₹2,50,000 + GST, one-time',
        why: 'You named behaviour no platform will let you build. That is the honest reason to code it — and it runs on your own server with no monthly fee to anyone.' };
    }
    const wooish = ['coupons', 'cod', 'returns', 'tracking', 'bookings', 'own'].filter(has).length;
    if (wooish >= 1) {
      return { to: '/pricing/websites?track=wp-ecom', label: 'See the WooCommerce build',
        title: 'WooCommerce with our plugin', price: '₹48,000 + GST, one-time',
        why: 'Coupons, loyalty, partial COD, returns and order tracking are exactly what our plugin covers. Bought as Shopify apps they are a monthly bill that never stops; here it is one payment and the installation is yours.' };
    }
    if (has('fast') || has('small')) {
      return { to: '/pricing/websites?track=shopify-ecom', label: 'See the Shopify build',
        title: 'Shopify store', price: 'From ₹75,000 + GST, plus Shopify’s monthly fee',
        why: 'A straightforward catalogue with nothing unusual, and speed matters most — that is the one case where renting the platform is the right call. Read the monthly-fee table before you commit.' };
    }
    return { to: '/pricing/websites?track=wp-ecom', label: 'See the WooCommerce build',
      title: 'WooCommerce with our plugin', price: '₹48,000 + GST, one-time',
      why: 'For a store you intend to keep, buying the installation once beats renting it every month — and our plugin covers what Shopify would charge you in apps.' };
  }

  const appish = ['portal', 'integrate', 'custom'].filter(has).length;
  if (appish >= 1) {
    return { to: '/pricing/websites?track=coded-generic', label: 'See the custom-coded option',
      title: 'Custom-coded', price: 'From ₹2,50,000 + GST',
      why: 'A portal, an integration or behaviour that is entirely yours is software with a website attached, not a website. It is quoted against what it has to do.' };
  }
  const heavy = ['catalogue', 'events', 'routing', 'multiadmin'].filter(has).length;
  if (heavy >= 1) {
    return { to: '/pricing/websites?track=wp-generic', label: 'See the three WordPress plans',
      title: 'WordPress — Advance', price: '₹45,000 + GST, one-time',
      why: 'A catalogue, event registration, routed enquiries or several staff logging in separately all sit in the Advance tier. Everything below it is included.' };
  }
  if (has('blog') || has('gallery')) {
    return { to: '/pricing/websites?track=wp-generic', label: 'See the three WordPress plans',
      title: 'WordPress — Mid', price: '₹35,000 + GST, one-time',
      why: 'Content you intend to keep publishing, and sections your own team edits, is the Mid tier. It is where most businesses land.' };
  }
  return { to: '/pricing/websites?track=wp-generic', label: 'See the three WordPress plans',
    title: 'WordPress — Short', price: '₹25,000 + GST, one-time',
    why: 'A credible presence with an enquiry route, and nothing you are paying for that you would not use.' };
}

export default function PricingFinder({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const [step, setStep] = useState<'root' | 'industry' | 'features' | 'rec' | 'crm'>('root');
  const [industry, setIndustry] = useState('');
  const [fns, setFns] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', k);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', k); document.body.style.overflow = prev; };
  }, [open, onClose]);

  useEffect(() => { if (open) { setStep('root'); setIndustry(''); setFns([]); } }, [open]);
  if (!open) return null;

  const go = (to: string) => { onClose(); nav(to); };
  const Opt = ({ b, s: sub2, extra, rec, onClick }:
    { b: string; s: string; extra?: string; rec?: boolean; onClick: () => void }) => (
    <button className="opt" type="button" onClick={onClick}>
      {rec && <span className="tagrec">RECOMMENDED</span>}
      <b>{b}</b><span>{sub2}</span>{extra && <em>{extra}</em>}
    </button>
  );

  const industryLabel = INDUSTRIES.find((i) => i.id === industry)?.b ?? '';

  /* every platform available to the chosen industry, so the suggestion can be overridden */
  const ALTERNATIVES: Record<string, { b: string; s: string; extra: string; to: string }[]> = {
    ngo: [
      { b: 'Give Setu', s: 'Donations, 80G receipts, donors, members, compliance — website included', extra: 'From ₹25,000 / year', to: '/pricing/ngo-os' },
      { b: 'NGO WordPress', s: '18 pages, 35 features, Razorpay donations. Bought once, owned outright', extra: '₹35,000 + GST', to: '/pricing/ngo-website' },
      { b: 'Compare all three', s: 'WordPress, Give Setu and custom-coded side by side, line by line', extra: 'All prices on one page', to: '/pricing/ngo-website' },
    ],
    ecom: [
      { b: 'Shopify', s: 'Fastest to launch, and a monthly fee for as long as you trade', extra: 'From ₹75,000 + GST', to: '/pricing/websites?track=shopify-ecom' },
      { b: 'WooCommerce', s: 'Our plugin replaces the paid apps. One payment, and it is yours', extra: '₹48,000 + GST', to: '/pricing/websites?track=wp-ecom' },
      { b: 'Custom-coded', s: 'Spring Boot and React on your own server, no platform limits', extra: '₹2,50,000 + GST', to: '/pricing/websites?track=coded-ecom' },
    ],
    other: [
      { b: 'WordPress', s: 'Three sizes — Short, Mid and Advance. One-time, and you own it', extra: '₹25,000 – ₹45,000', to: '/pricing/websites?track=wp-generic' },
      { b: 'Custom-coded', s: 'When the site is really an application — a portal, an integration', extra: 'From ₹2,50,000', to: '/pricing/websites?track=coded-generic' },
    ],
  };

  return (
    <div className="pfx" role="dialog" aria-modal="true" aria-label="Find your pricing"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <button className="x" onClick={onClose} aria-label="Close">×</button>

      <div className="sheet">
        {step === 'root' && (
          <>
            <p className="kick">Pricing</p>
            <h2>What are you pricing?</h2>
            <p className="sub">Pick the closest one. Every route ends on a page with real numbers on it —
              no ranges, and nothing hidden behind a form.</p>
            <div className="opts">
              <Opt b="A website" s="For a non-profit, a store, or any other industry" extra="From ₹25,000" onClick={() => setStep('industry')} />
              <Opt b="Social media" s="Management retainers and content plans" onClick={() => go('/pricing/social-media')} />
              <Opt b="A CRM" s="Sales and operations software built for your industry" onClick={() => setStep('crm')} />
            </div>
          </>
        )}

        {step === 'crm' && (
          <>
            <p className="crumbs"><b>CRM</b></p>
            <h2>Which operation are you running?</h2>
            <p className="sub">These are systems we have already built and run in production. If yours is
              not here, it is a scoping conversation rather than a package.</p>
            <div className="opts">
              <Opt b="Construction" s="Leads, site progress, payments and client updates" onClick={() => go('/services/ci-crm')} />
              <Opt b="Hotels" s="Folio, bookings, guests and billing" onClick={() => go('/services/hotel-crm')} />
              <Opt b="Car wash" s="Jobs, packages, staff and repeat customers" onClick={() => go('/services/car-wash-crm')} />
              <Opt b="Something else" s="Tell us the operation and we will quote against it" onClick={() => go('/contact-us/')} />
            </div>
            <div className="bar"><button className="lnk" onClick={() => setStep('root')}>← Back</button></div>
          </>
        )}

        {step === 'industry' && (
          <>
            <p className="crumbs"><b>Website</b></p>
            <h2>Which industry is it for?</h2>
            <p className="sub">A site that issues 80G receipts and one that takes orders are different
              builds. Tell us the industry and the next question gets specific.</p>
            <div className="opts">
              {INDUSTRIES.map((i) => (
                <Opt key={i.id} b={i.b} s={i.s} onClick={() => { setIndustry(i.id); setFns([]); setStep('features'); }} />
              ))}
            </div>
            <div className="bar"><button className="lnk" onClick={() => setStep('root')}>← Back</button></div>
          </>
        )}

        {step === 'features' && (
          <>
            <p className="crumbs">Website · <b>{industryLabel}</b></p>
            <h2>What does it actually have to do?</h2>
            <p className="sub">Pick everything that applies. The recommendation is worked out from these,
              not from what we would prefer to sell.</p>
            <div className="grid3">
              {(FEATURES[industry] ?? []).map((f) => {
                const on = fns.includes(f.id);
                return (
                  <button key={f.id} type="button" className={`chk${on ? ' on' : ''}`} aria-pressed={on}
                    onClick={() => setFns(on ? fns.filter((x) => x !== f.id) : [...fns, f.id])}>
                    <i>{on ? '✓' : ''}</i><span>{f.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="bar">
              <button className="go" disabled={!fns.length} onClick={() => setStep('rec')}>Show me what fits</button>
              <button className="lnk" onClick={() => setStep('industry')}>← Back</button>
            </div>
          </>
        )}

        {step === 'rec' && (() => {
          const r = recommend(industry, fns);
          const alts = (ALTERNATIVES[industry] ?? []).filter((a) => !r.title.startsWith(a.b));
          return (
            <>
              <p className="crumbs">Website · {industryLabel} · <b>Our recommendation</b></p>
              <h2>{r.title}</h2>
              <div className="rec">
                <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#d8c48d' }}>{r.price}</div>
                <p className="why">{r.why}</p>
                <div className="bar">
                  <button className="go" onClick={() => go(r.to)}>{r.label}</button>
                  <button className="lnk" onClick={() => setStep('features')}>← Change my answers</button>
                </div>
              </div>
              {alts.length > 0 && (
                <>
                  <p className="sub" style={{ margin: '26px 0 12px' }}>
                    The other routes for {industryLabel.toLowerCase()}, in case we read you wrong:
                  </p>
                  <div className="opts">
                    {alts.map((a) => <Opt key={a.b} b={a.b} s={a.s} extra={a.extra} onClick={() => go(a.to)} />)}
                  </div>
                </>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}
